import { useEffect, useState } from "react";
import { Leaf, Route } from "lucide-react";
import { api } from "@/lib/api";
import { colorOf } from "@/lib/habitColors";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const weeksSince = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / (7 * 864e5))) : null);

/** Thin line with soft dots: past stages carry a leaf, the current one is highlighted. No durations are judged. */
export function StageTimeline({ journey: j, showWeeks = false, testid }) {
  if (!j) return null;
  const c = colorOf({ color: j.color });
  return (
    <div data-testid={testid || `stage-timeline-${j.habit_id}`} className="space-y-1">
      <div className="flex items-center">
        {j.stages.map((s, i) => (
          <div key={i} className="flex items-center flex-1 min-w-0">
            <div title={`${s.name}${s.entered_at ? ` · since ${fmt(s.entered_at)}` : ""}`} data-testid={`stage-dot-${j.habit_id}-${i}`}
              className={`w-4 h-4 rounded-full grid place-items-center shrink-0 ${s.state === "past" ? c.solid : s.state === "current" ? `${c.solid} ring-4 ${c.ring}` : "bg-stone-200"}`}>
              {s.state === "past" && <Leaf className="w-2.5 h-2.5 text-white" />}
            </div>
            {i < j.stages.length - 1 && <div className={`h-0.5 flex-1 ${s.state === "past" ? c.dot : "bg-stone-200"}`} />}
          </div>
        ))}
      </div>
      <div className="flex text-[11px] text-stone-600">
        {j.stages.map((s, i) => (
          <div key={i} className={`flex-1 min-w-0 truncate ${s.state === "current" ? "font-semibold text-stone-900" : ""}`}>{s.name}{s.entered_at && s.state !== "future" ? <span className="text-stone-400"> · {s.state === "current" ? "since " : ""}{fmt(s.entered_at)}</span> : ""}</div>
        ))}
      </div>
      {showWeeks && j.current_since != null && <div className="text-[11px] text-stone-500" data-testid={`stage-weeks-${j.habit_id}`}>{weeksSince(j.current_since)} week{weeksSince(j.current_since) === 1 ? "" : "s"} in the current stage</div>}
    </div>
  );
}

/** Progress tab: every staged habit's path. */
export function StageJourneyCard() {
  const [journeys, setJourneys] = useState(null);
  useEffect(() => { api.get("/habits/stage-journey").then((r) => setJourneys(r.data.journeys)).catch(() => setJourneys([])); }, []);
  if (!journeys?.length) return null;
  return (
    <section className="warm-card p-6 space-y-5" data-testid="stage-journey-card">
      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-500"><Route className="w-3.5 h-3.5" /> Stage journey</div>
      {journeys.map((j) => (
        <div key={j.habit_id} className="space-y-2">
          <h3 className="font-display font-bold text-stone-900">{j.title}</h3>
          <StageTimeline journey={j} />
        </div>
      ))}
    </section>
  );
}
